import React from "react";

/**
 * Shared presentational frame for the report tables: a padded wrapper, an
 * optional title, and a bordered/rounded horizontally-scrollable box. Gives the
 * department-report (ReportTreeTable) and the finance-tab tables one consistent
 * "look". Visuals live in the `.ntcog-report-frame*` rules in global.css.
 *
 * Pass `freeze` only for the department tree table so Finance tables do not
 * get a forced max-height viewport.
 *
 * Header layout: left = title and/or toolbarStart (filters); right = toolbar
 * (e.g. Expand all / Collapse all).
 */
type Props = {
  title?: React.ReactNode;
  toolbarStart?: React.ReactNode;
  toolbar?: React.ReactNode;
  freeze?: boolean;
  scrolled?: boolean;
  boxRef?: React.Ref<HTMLDivElement>;
  style?: React.CSSProperties;
  children?: React.ReactNode;
};

export default function ReportTableFrame({
  title,
  toolbarStart,
  toolbar,
  freeze = false,
  scrolled = false,
  boxRef,
  style,
  children,
}: Props) {
  const className = [
    "ntcog-report-frame",
    freeze ? "ntcog-report-frame--freeze" : "",
    freeze && scrolled ? "is-scrolled" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const showHeader = Boolean(title || toolbarStart || toolbar);

  return (
    <div className={className} style={style}>
      {showHeader ? (
        <div className="ntcog-report-frame__header">
          {title || toolbarStart ? (
            <div className="d-flex flex-wrap align-items-center gap-2">
              {title ? <div className="ntcog-report-frame__title">{title}</div> : null}
              {toolbarStart}
            </div>
          ) : null}
          {toolbar}
        </div>
      ) : null}
      <div ref={boxRef} className="ntcog-report-frame__box">
        {children}
      </div>
    </div>
  );
}
